import { useCallback, useState } from 'react';

import {
	EditSquareFilledIcon,
	EditSquareIcon,
	GovBuildingFilledIcon,
	GovBuildingIcon,
	HomeFilledIcon,
	HomeIcon,
	SpeakerFilledIcon,
	SpeakerIcon,
} from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import {
	SideNavigation,
	SideNavigationContentSection,
	SideNavigationItem,
	SideNavigationLayout,
	SideNavigationMainContent,
} from '@change/ds-layout/next';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2BreakpointState } from 'src/app/pages/starterDashboardV2/pageContext';
import {
	DM_TAB_CLICK,
	HOME_TAB_CLICK,
	SHARE_TAB_CLICK,
	SUPPORTERS_TAB_CLICK,
} from 'src/app/pages/starterDashboardV2/shared/constants';
import { ButtonVariantProvider } from 'src/app/pages/starterDashboardV2/shared/contexts';

import { useDashboardReskinExperiment } from '../../hooks/useDashboardReskinExperiment';

import { DesktopTabs } from './components/DashboardTabs/components/DesktopTabs';
import { DecisionMakersList } from './components/DecisionMakersList';
import { RecentSupporters } from './components/EngageSupporters/components/RecentSupporters';
import { GrowPetition } from './components/GrowPetition';
import { NotificationsCenter } from './components/NotificationsCenter';
import { PetitionInsights } from './components/PetitionInsights';
import { PetitionOverview } from './components/PetitionOverview';
import { ReadyToShare } from './components/ReadyToShare';
import { Recommendations } from './components/Recommendations';
import { StarterDashboardComments } from './components/StarterDashboardComments';
import { StepsToVictory } from './components/StepsToVictory';
import { UpdateSupporters } from './components/UpdateSupporters';
import { PageBodyReskin } from './index_new';

const TAB_CLICK_EVENTS: Record<string, string> = {
	home: HOME_TAB_CLICK,
	share: SHARE_TAB_CLICK,
	dms: DM_TAB_CLICK,
	supporters: SUPPORTERS_TAB_CLICK,
};

const DesktopNavigation = () => {
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const track = useTracking();
	const switchTab = useCallback(
		(currentTab: string, previousTab: string) => {
			const event = TAB_CLICK_EVENTS[currentTab];
			void track(event, {
				context: previousTab,
			});
		},
		[track],
	);

	const [commentsVisibility, setCommentsVisibility] = useState(true);

	return (
		<SideNavigationLayout className="hidden md:block" onTabChange={switchTab} data-testid="desktop-navigation">
			<SideNavigation>
				<SideNavigationItem selectedIcon={<HomeFilledIcon />} icon={<HomeIcon />} id="home" label="Home" />
				<SideNavigationItem
					selectedIcon={<SpeakerFilledIcon />}
					icon={<SpeakerIcon />}
					id="share"
					label="Share your petition"
				/>
				<SideNavigationItem
					selectedIcon={<GovBuildingFilledIcon />}
					icon={<GovBuildingIcon />}
					id="dms"
					label="Connect to decision makers"
				/>
				<SideNavigationItem
					selectedIcon={<EditSquareFilledIcon />}
					icon={<EditSquareIcon />}
					id="supporters"
					label="Message your supporters"
				/>
			</SideNavigation>
			<SideNavigationMainContent>
				<SideNavigationContentSection id="home" className="flex flex-col gap-6 pr-4">
					<NotificationsCenter />
					<PetitionOverview />
					<Recommendations slug={slug} viewContext="home" />
					{/* eslint-disable-next-line @typescript-eslint/naming-convention */}
					<div className={cn('mb-4 grid grid-cols-[1fr] gap-x-4', { 'md:grid-cols-[1fr_1fr]': commentsVisibility })}>
						<RecentSupporters />
						{commentsVisibility && <StarterDashboardComments setCommentsVisibility={setCommentsVisibility} />}
					</div>
					<PetitionInsights />
				</SideNavigationContentSection>

				<SideNavigationContentSection id="share" className="flex flex-col gap-8 pr-4">
					<ReadyToShare />
					<Recommendations slug={slug} viewContext="share" />
					<GrowPetition />
					<StepsToVictory />
				</SideNavigationContentSection>

				<SideNavigationContentSection id="dms" className="flex flex-col gap-8 pr-4">
					<DecisionMakersList />
				</SideNavigationContentSection>

				<SideNavigationContentSection id="supporters" className="flex flex-col gap-8 pr-4">
					<UpdateSupporters />
				</SideNavigationContentSection>
			</SideNavigationMainContent>
		</SideNavigationLayout>
	);
};

const MobileNavigation = () => {
	return (
		<main className="col-span-4 md:hidden" data-testid="page-body-control">
			{/* TODO: Remove this provider once the tabs component has been migrated to sunrise */}
			{/* Naming is deceiving, even though the component's name is **Desktop** Tabs, we need to use it to render tabs in mobile */}
			<DesktopTabs />
		</main>
	);
};

export function PageBody() {
	const {
		data: { isEnabled: isReskinEnabled },
	} = useDashboardReskinExperiment();
	const { isMobile } = useStarterDashboardV2BreakpointState();

	if (isReskinEnabled) {
		return <PageBodyReskin />;
	}

	return (
		<ButtonVariantProvider data-qa="button-variant-context">
			{isMobile ? (
				<MobileNavigation data-test-id="mobile-navigation" />
			) : (
				<DesktopNavigation data-testid="desktop-navigation" />
			)}
		</ButtonVariantProvider>
	);
}
